/**
 * NavRestructureNotice — M-UX-2.1 Risiko-Minderung (`plan-ux-2.1-nav-sektionen.md`
 * §4): einmaliger, wegklickbarer In-App-Hinweis auf die neue, aufgaben-
 * gruppierte Menüstruktur (Umgewöhnungs-Risiko der Umgruppierung).
 *
 * Persistenz: `localStorage`-Key `faebill.navRestructureNoticeDismissed`
 * (Wert `'1'`). Klick auf „Verstanden" schreibt den Key UND versteckt die
 * Leiste sofort lokal (kein Re-Mount nötig, um wieder zu verschwinden).
 *
 * Eigene, kleine Komponente (statt inline in `MainLayout.tsx`) — hält
 * `MainLayout` schlank (Hot-File, s. `plan-ux-2.1-nav-sektionen.md` §0/§5) und
 * macht den Hinweis unabhängig testbar.
 *
 * a11y/i18n: `role="status"` (nicht-blockierender Hinweis), alle Strings über
 * `t(..., { ns: 'navigation' })` — kein hartkodierter deutscher Text. SSR-/
 * Jest-defensiv: `localStorage`-Zugriff nur hinter `typeof window !== 'undefined'`.
 *
 * @module components/layout/NavRestructureNotice
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconInfo } from '../../icons';

/** localStorage-Key, unter dem das Dismiss dauerhaft persistiert wird. */
const NAV_RESTRUCTURE_NOTICE_DISMISSED_KEY = 'faebill.navRestructureNoticeDismissed';

/**
 * Liest den Dismiss-Zustand defensiv (SSR/Jest ohne `window`/`localStorage`
 * → Notice wird konservativ als NICHT-dismissed behandelt, damit ein
 * fehlender `localStorage`-Zugriff nie einen Laufzeitfehler wirft).
 */
function readNoticeDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(NAV_RESTRUCTURE_NOTICE_DISMISSED_KEY) === '1';
  } catch {
    // localStorage kann in restriktiven Umgebungen (Privacy-Mode, o.ä.) werfen —
    // fail-closed zu "nicht dismissed" ist harmloser als ein kaputter Header.
    return false;
  }
}

/** Persistiert das Dismiss. Fehlerhafte Schreibversuche werden verschluckt (kein `console.log`). */
function markNoticeDismissed(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(NAV_RESTRUCTURE_NOTICE_DISMISSED_KEY, '1');
  } catch {
    // s. readNoticeDismissed — bewusst still, die lokale UI-State-Änderung
    // (setDismissed(true)) blendet die Leiste trotzdem für diese Session aus.
  }
}

/**
 * Dünne, dismissbare Info-Leiste unter dem Dashboard-Header. Rendert `null`,
 * sobald der Nutzer sie einmal geschlossen hat (persistiert), initial und bei
 * jedem Mount neu geprüft.
 */
const NavRestructureNotice: React.FC = () => {
  const { t } = useTranslation('navigation');
  const [dismissed, setDismissed] = useState<boolean>(readNoticeDismissed);

  if (dismissed) return null;

  const handleDismiss = () => {
    markNoticeDismissed();
    setDismissed(true);
  };

  return (
    <div
      role="status"
      className="border-b border-[var(--tone-accent-border)] bg-[var(--tone-accent-bg)] px-4 py-2"
    >
      <div className="container mx-auto flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-2">
          <span className="mt-0.5 flex-shrink-0 text-accent-ink">
            <IconInfo />
          </span>
          <div>
            <p className="text-xs font-semibold text-[var(--tone-accent-fg)]">
              {t('navNoticeTitle')}
            </p>
            <p className="text-xs text-[var(--tone-accent-fg)]">{t('navNoticeBody')}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="flex-shrink-0 self-end rounded-md bg-indigo-600 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-indigo-500 sm:self-auto"
        >
          {t('navNoticeDismiss')}
        </button>
      </div>
    </div>
  );
};

export default NavRestructureNotice;
